'use client'

import { useEffect, useState } from 'react'
import { useAuthProvider } from '../providers/AuthProvider'
import { useAuthenticationDialog } from '../providers/AuthenticationDialogProvider'
import {
  PENDING_AUTH_ACTION_EVENT,
  takePendingAuthAction,
  type PendingAuthAction,
  type PendingAuthActionKind,
} from '../lib/pendingAuthAction'

type UseResumeAfterSignInParams = {
  kind: PendingAuthActionKind
  sku?: string
  // The feature's own data for this user has loaded (cart, saves, ...), so the
  // resumed action sees what the user already has.
  ready?: boolean
  onResume: (action: PendingAuthAction) => void
}

// Carries on with what a guest tapped once they have signed in. Waits for the
// sign-in sheet to close so the next sheet doesn't open on top of it.
export function useResumeAfterSignIn({
  kind,
  sku,
  ready = true,
  onResume,
}: UseResumeAfterSignInParams): void {
  const { status } = useAuthProvider()
  const { isOpen } = useAuthenticationDialog()
  const [savedCount, setSavedCount] = useState(0)

  useEffect(() => {
    function savedHandler() {
      setSavedCount((count) => count + 1)
    }

    window.addEventListener(PENDING_AUTH_ACTION_EVENT, savedHandler)

    return () => {
      window.removeEventListener(PENDING_AUTH_ACTION_EVENT, savedHandler)
    }
  }, [])

  useEffect(() => {
    if (status !== 'authenticated' || isOpen || !ready) {
      return
    }

    const action = takePendingAuthAction(kind, sku)

    if (action) {
      onResume(action)
    }
  }, [status, isOpen, ready, kind, sku, onResume, savedCount])
}
